/**
 * The marked set for the collections tree.
 *
 * ── The distinction the whole feature rests on ───────────────────────────────
 *   `selectedRequest` / `selectedCollection` / `selectedFolder`  =  what is OPEN
 *   `markedIds` (here)                                           =  what is MARKED
 *
 * They are NOT the same thing and must never be merged. Opening a request is
 * async and side-effectful — `useCollections.selectRequest` clears three other
 * selections, fetches the full record over HTTP and opens a tab. If a
 * modifier-click reached it, ctrl-clicking six rows would fire six API calls and
 * open six tabs. So `handleRowClick` returns `true` for every marking gesture and
 * the row must then NOT open. That contract is pinned by a test.
 *
 * ── Gestures (owner decision D2, stated unprompted) ──────────────────────────
 * A bare click NEVER marks a second item. It opens the row exactly as it always
 * has, and collapses the marked set to that one row. Marking is ctrl/cmd (toggle)
 * or shift (range over the VISIBLE row order) only. No click-to-add, no
 * rubber-band marquee, no long-press.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { rangeBetween } from '../utils/treeSelection';
import { isMacPlatform } from '../utils/keyboard';

const EMPTY = new Set();

export function useTreeMultiSelect({ visibleRows, escapeEnabled = true } = {}) {
  const [markedIds, setMarkedIds] = useState(EMPTY);
  const anchorRef = useRef(null);

  // The row order is read at CLICK time, not captured in the callback's closure,
  // so `handleRowClick` keeps a stable identity and the rows' React.memo holds.
  const rowsRef = useRef(visibleRows);
  rowsRef.current = visibleRows;

  // Mirror of the set, so the Escape handler can decide SYNCHRONOUSLY whether it
  // has anything to consume (a functional setState cannot answer that in time to
  // stop the event).
  const markedRef = useRef(markedIds);
  markedRef.current = markedIds;

  const clear = useCallback(() => {
    anchorRef.current = null;
    setMarkedIds((prev) => (prev.size === 0 ? prev : EMPTY));
  }, []);

  const markOnly = useCallback((id) => {
    anchorRef.current = id;
    setMarkedIds((prev) => (prev.size === 1 && prev.has(id) ? prev : new Set([id])));
  }, []);

  /**
   * @returns {boolean} true when the click was a MARKING gesture and the caller
   *                    must not open the row.
   */
  const handleRowClick = useCallback(
    (e, id) => {
      // A right-click must never be read as a marking gesture. On macOS that is
      // not hypothetical: **Ctrl+click IS the right-click**, so treating Ctrl as
      // "add to selection" there means the trailing `click` toggles the row the
      // user just opened the menu on, silently dropping it out of the selection
      // — reported from a MacBook as "it says 1 selected". So: ⌘ on macOS, Ctrl
      // or ⌘ elsewhere. `button === 2` is belt-and-braces for any browser that
      // does deliver a click for the secondary button.
      // A secondary click is CONSUMED here: it neither marks nor opens, and it
      // leaves the marked set exactly as it was for `handleContextMenu` to act
      // on. Returning `true` is what stops the caller opening the row.
      //
      // On macOS this is not an edge case — **Ctrl+click IS the right-click**,
      // and the browser delivers a trailing `click` carrying ctrlKey after the
      // `contextmenu`. Reading that as "add to selection" toggled the row the
      // menu had just opened on; reading it as a plain click was worse, since a
      // plain click collapses the set to one. Both were reported from a MacBook
      // as "it says 1 selected". ⌘ is the macOS multi-select modifier, not Ctrl.
      if (e?.button === 2 || (isMacPlatform() && e?.ctrlKey)) return true;
      const additive = isMacPlatform() ? !!e?.metaKey : !!(e?.ctrlKey || e?.metaKey);
      const ranged = !!e?.shiftKey;

      if (!additive && !ranged) {
        markOnly(id);
        return false;
      }

      // preventDefault kills the browser's own shift/ctrl text selection, which
      // would otherwise paint over the whole tree as the user extends a range.
      e.preventDefault?.();
      e.stopPropagation?.();

      if (ranged) {
        const span = rangeBetween(rowsRef.current || [], anchorRef.current, id);
        setMarkedIds((prev) => (additive ? new Set([...prev, ...span]) : new Set(span)));
        // The anchor deliberately survives a shift-click, so extending a range
        // repeatedly keeps growing from the same origin.
        if (anchorRef.current == null) anchorRef.current = id;
        return true;
      }

      setMarkedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      anchorRef.current = id;
      return true;
    },
    [markOnly]
  );

  // Escape clears the selection.
  //
  // On WINDOW, with capture — never `document`. `ui/Modal.js` registers a
  // document-capture Escape handler that calls stopImmediatePropagation(), so a
  // second document listener wins or loses purely on registration order, and any
  // remount flips the outcome (this is exactly how Escape once discarded unsaved
  // calendar edits). Capture travels window → document → target, so listening on
  // window is ordered by PHASE and cannot lose that race.
  //
  // The event is consumed ONLY when there is actually a selection to clear, so
  // Escape keeps closing whatever is on top the rest of the time. `escapeEnabled`
  // is how the sidebar hands Escape back while one of ITS OWN selection modals is
  // open — otherwise the confirm dialog for a bulk delete could not be dismissed.
  useEffect(() => {
    if (!escapeEnabled) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Only a DELIBERATE multi-selection may take Escape. A plain click marks
      // one row, so `size !== 0` armed this after every ordinary click on the
      // tree — and because it sits on window CAPTURE with
      // stopImmediatePropagation, it then outranked `ui/Modal.js` (document
      // capture) and the drag hook (window bubble) and ate Escape app-wide.
      // One incidentally-marked row is not worth that. Found in the
      // pre-release review of #171.
      if (markedRef.current.size < 2) return;
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      anchorRef.current = null;
      setMarkedIds(EMPTY);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [escapeEnabled]);

  return { markedIds, handleRowClick, markOnly, clear };
}
